import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from 'src/api/client';
import { useToast } from 'src/Contexts/ToastContext';
import './FilePreview.css';

export const FilePreview = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const [imageData, setImageData] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filename, setFilename] = useState('');
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!fileId) {
      toast.error('Invalid file ID');
      navigate('/');
      return;
    }

    const fetchFile = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the file as blob (api.get unwraps res.data)
        const blob: Blob = await api.get(`/api/v2/files/${fileId}`, {
          responseType: 'blob',
        });

        // Extract filename from blob type or use fallback
        setFilename(fileId);

        const objectUrl = URL.createObjectURL(blob);
        setImageData(objectUrl);
        setLoading(false);
      } catch (err) {
        setError(err as Error);
        setLoading(false);
        toast.error('Failed to load file: ' + (err as Error).message);
        console.error('File preview error:', err);
      }
    };

    void fetchFile();
  }, [fileId, toast, navigate]);

  return (
    <div className="file-preview-page">
      <div className="file-preview-header">
        <h2>File Preview</h2>
        <button onClick={() => navigate(-1)} className="back-button">
          Back
        </button>
      </div>

      <div className="file-preview-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner" />
            <p>Loading preview...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <h3>Error Loading File</h3>
            <p>{error.message}</p>
            <button onClick={() => navigate('/')} className="home-button">
              Return Home
            </button>
          </div>
        ) : imageData ? (
          <div className="preview-container">
            <div className="filename-bar">
              <span>{filename}</span>
            </div>
            <div className="image-preview">
              <img src={imageData} alt={filename} className="preview-image" />
            </div>
            <div className="preview-actions">
              <button
                onClick={() => {
                  // Download the image
                  const link = document.createElement('a');
                  link.href = imageData;
                  link.download = filename;
                  link.click();
                }}
                className="download-button"
              >
                Download
              </button>
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <p>No preview available</p>
          </div>
        )}
      </div>
    </div>
  );
};
